import React from 'react';
import {
	IBlockData,
	BasicType,
	AdvancedType,
	createCustomBlock,
	RecursivePartial,
} from '../../..';
import { BasicBlock } from '../../../components/BasicBlock';
import { resolvePreviewColor } from '@core/utils';
import { buildListingDataAttr } from '../../../utils/listingDataAttr';
import {
	isListingUrlTag,
	previewListingUrl,
	resolveListingHref,
	stripEmptyListingHrefs,
} from '../../../utils/resolveListingHref';
import {
	getListing,
	isListingLoading,
	isListingUnavailable,
} from '../../../utils/listingCache';
import { merge } from 'lodash';
import * as Handlebars from 'handlebars';
import { singleListingLayouts } from '../../../data';
import { getParentByIdx } from '../../../utils/block';
import { processHtmlMergeTags } from '../../../utils/getMergeTagImageUrl';
import { getProxiedImageUrl } from '../../../utils/getProxiedImageUrl';
import { getListingPlaceholderImage } from '../../../utils/getImg';
import { registerDynamicTextHandlebarsHelpers } from '../../../utils/dynamicTextFormatHelpers';

// Register the raw helper for Handlebars to enable {{{{raw}}}} syntax
Handlebars.registerHelper('raw', function (options) {
	return options.fn();
});
registerDynamicTextHandlebarsHelpers(Handlebars);

interface SingleListingItem {
	id: string;
	title?: string;
	address?: string;
	streetaddress?: string;
	city?: string;
	stateProvince?: string;
	state?: string;
	postalCode?: string;
	zip_code?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	url?: string;
	listingurl?: string;
}

interface ISingleListingData {
	/** The binding. This is the ONLY listing state that gets persisted. */
	selectedListingId?: string;
	/**
	 * @deprecated Resolved listing values are no longer saved — a stored copy
	 * goes stale the moment the listing changes price, status, or photos.
	 * Read at load time to recover the id from pre-migration designs, then
	 * dropped. Never write it.
	 */
	selectedListing?: SingleListingItem | null;
	layout?: 'stacked' | 'card';
	placeholderImage?: string;
}

/**
 * Designs saved before bindings were reference-only carry the whole listing
 * object and sometimes no id at all. Recover the id from whichever field has it.
 */
export function getSingleListingId(
	value?: ISingleListingData | null,
): string | undefined {
	return value?.selectedListingId || value?.selectedListing?.id || undefined;
}

interface ISingleListingAttributes extends Record<string, any> {
	'background-color': string;
	'listing-name-color': string;
	'listing-price-color': string;
	'listing-address-color': string;
	'listing-details-color': string;
	'title-font-size': string;
	'price-font-size': string;
	'address-font-size': string;
	'button-color': string;
	'button-text-color': string;
	'button-font-size': string;
	'button-text': string;
	'show-title': boolean;
	'show-address': boolean;
	'show-price': boolean;
	'show-details': boolean;
	padding: string;
	title: string;
}

export type ISingleListing = IBlockData<ISingleListingAttributes, ISingleListingData>;

const CustomBlockType = {
	SINGLE_LISTING_BLOCK: 'text_single_listing' as const,
};

export const SingleListingBlock = createCustomBlock<ISingleListing>({
	name: 'Single',
	type: CustomBlockType.SINGLE_LISTING_BLOCK,
	validParentType: [
		// Allow placement like Text/Image/Button so DnD can target wrapper/section/page
		BasicType.PAGE,
		BasicType.WRAPPER,
		BasicType.SECTION,
		BasicType.GROUP,
		BasicType.COLUMN,
		BasicType.HERO,
		AdvancedType.WRAPPER,
		AdvancedType.SECTION,
		AdvancedType.GROUP,
		AdvancedType.COLUMN,
		AdvancedType.HERO,
	],
	create: (payload?: RecursivePartial<ISingleListing>) => {
		const defaultData: ISingleListing = {
			type: CustomBlockType.SINGLE_LISTING_BLOCK,
			data: {
				value: {
					selectedListingId: '1',
					layout: 'stacked',
					placeholderImage: getProxiedImageUrl(
						getListingPlaceholderImage(
							`placeholder_${Math.floor(Math.random() * 8) + 1}`,
						),
						'250',
						'167',
						'https://placehold.co/300x200/e1e1e1/666666?text=No+Image',
					),
				},
			},
			attributes: {
				'background-color': 'Transparent',
				'listing-name-color': '#000000',
				'listing-price-color': '#000000',
				'listing-address-color': '#000000',
				'listing-details-color': '#666666',
				'title-font-size': '16px',
				'price-font-size': '18px',
				'address-font-size': '14px',
				'button-color': '#007bff',
				'button-text-color': '#ffffff',
				'button-font-size': '14px',
				'button-text': 'View Listing',
				'show-title': true,
				'show-address': true,
				'show-price': true,
				'show-details': true,
				padding: '0px 0px 0px 0px',
				title: 'Single',
				href: '{{listing.url}}',
				target: '_blank',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	render: (params: {
		data: ISingleListing;
		idx?: string | null;
		mode: 'testing' | 'production';
		context?: any;
		dataSource?: { [key: string]: any };
		children?: React.ReactNode;
		keepClassName?: boolean;
		renderPortal?: any;
	}) => {
		const { data, idx } = params;
		const boundListingId = getSingleListingId(data.data.value);
		const selectedListingId = boundListingId || '1';
		const selectedLayout = data.data.value.layout || 'stacked';
		// Resolve the binding live. `getListing` is a synchronous cache read that
		// schedules a fetch on a miss; the canvas re-renders when it lands, so a
		// design never displays listing values frozen at save time.
		//
		// The legacy `selectedListing` object is only a first-paint stand-in for
		// designs saved before bindings were reference-only — the resolved copy
		// replaces it as soon as the lookup returns.
		const resolvedListing = boundListingId ? getListing(boundListingId) : undefined;
		const isResolving = isListingLoading(boundListingId);
		const isUnavailable = isListingUnavailable(boundListingId);
		// A listing that resolved to nothing renders its fallback, not stale data.
		const storedListing = isUnavailable
			? null
			: resolvedListing || data.data.value.selectedListing;

		// Get isDesignAdmin from dataSource for conditional rendering
		const isDesignAdmin = params.dataSource?.isDesignAdmin;

		// Determine the actual rendering mode
		// 'edit' = editor canvas (show merge tags)
		// 'preview' = preview/test modal (show actual data)
		// 'production' = final export (show merge tags)
		let actualMode: 'edit' | 'preview' | 'production';

		if (params.mode === 'production') {
			actualMode = 'production';
		} else if (params.dataSource?.previewMode) {
			// Preview/test modal should show actual data
			actualMode = 'preview';
		} else {
			// Editor canvas shows merge tags
			actualMode = 'edit';
		}

		// Resolution in flight and nothing cached yet — show that rather than a
		// flash of sample data that is about to be replaced.
		if (isResolving && !storedListing && actualMode !== 'production') {
			return (
				<BasicBlock
					params={params}
					tag='mj-text'
				>
					<div style={{ padding: '16px', textAlign: 'center', color: '#75777f' }}>
						Loading listing…
					</div>
				</BasicBlock>
			);
		}

		// Use stored listing data if available, otherwise fall back to mock data
		let displayListing: SingleListingItem | null = null;

		if (storedListing) {
			// Use the stored listing data from the selection
			displayListing = storedListing;
		} else {
			// Fall back to mock data for backwards compatibility
			const mockListings: SingleListingItem[] = [
				{
					id: '1',
					title: '12345-0 5th Ave',
					address: '12345-0 5th Ave',
					city: 'New York',
					stateProvince: 'NY',
					postalCode: '10001',
					list_price: 350000,
					beds: 3,
					baths: 2,
					sq_foot: 1800,
					image_data: [
						data.data.value.placeholderImage ||
							getListingPlaceholderImage('placeholder_1'),
					],
				},
			];

			// Find the selected listing from mock data
			const selectedListing = mockListings.find(
				listing => listing.id === selectedListingId,
			);

			// Use first listing as fallback
			displayListing = selectedListing || mockListings[0];
		}

		// Find the layout configuration
		const layoutConfig = singleListingLayouts.find(
			(layout: any) => layout.id === selectedLayout,
		);
		if (!layoutConfig) {
			console.error(`Layout "${selectedLayout}" not found`);
			return (
				<BasicBlock
					params={params}
					tag='mj-raw'
				>
					<div>Layout configuration not found</div>
				</BasicBlock>
			);
		}

		// Prepare handlebars data (single listing)
		if (!displayListing) {
			return (
				<BasicBlock
					params={params}
					tag='mj-raw'
				>
					<div>No listing selected</div>
				</BasicBlock>
			);
		}

		// In production or edit mode, use merge tags in DSA.
		// In preview mode and CBAY edit mode, use actual data.
		const shouldUseMergeTags =
			actualMode === 'production' || (actualMode === 'edit' && isDesignAdmin);

		// Generate required CSS classes for block selection
		const isTest = params.mode === 'testing';
		let cssClasses = 'email-block';
		if (isTest && idx) {
			cssClasses = `email-block node-idx-${idx} node-type-${data.type}`;
		}

		// Prepare template context with listing object structure matching template expectations
		const previewListingContext = {
			imagePath: (() => {
				const fallbackUrl = 'https://placehold.co/300x200/e1e1e1/666666?text=No+Image';
				const imageUrl = displayListing.image_data?.[0];
				const width = selectedLayout === 'stacked' ? '240' : undefined;
				const height = selectedLayout === 'stacked' ? '160' : undefined;
				if (!imageUrl || imageUrl.trim() === '' || imageUrl.includes('nophoto')) {
					// A real listing with no photo gets this block instance's own stock
					// placeholder (stable per block, set at create time) instead of the
					// plain gray "No Image" box, matching the backend's own fallback.
					return (
						data.data.value.placeholderImage ||
						getProxiedImageUrl(getListingPlaceholderImage('placeholder_1'), width, height, fallbackUrl)
					);
				}
				return getProxiedImageUrl(imageUrl, width, height, fallbackUrl);
			})(),
			// address/stateProvince/postalCode aren't real columns on the raw backend row -
			// the picker translates them from streetaddress/state/zip_code at selection
			// time, but that translation doesn't survive a reload, where this resolves
			// straight off the raw row (see resolveAttachedListingMergeTags.ts's
			// resolveByPath and dataFields.js's listing.address/listing.zip entries for the
			// same pattern). Fall back to the raw columns so a reopened design still shows
			// the full address, not just city.
			address: displayListing.address || displayListing.streetaddress || '',
			city: displayListing.city || '',
			stateProvince: displayListing.stateProvince || displayListing.state || '',
			postalCode: displayListing.postalCode || displayListing.zip_code || '',
			listPrice: displayListing.list_price
				? `$${displayListing.list_price.toLocaleString()}`
				: '$0',
			beds: displayListing.beds || 0,
			baths: displayListing.baths || 0,
			sqFoot: displayListing.sq_foot ? displayListing.sq_foot.toLocaleString() : '0',
			// listingurl is the composed absolute link; the raw url column is a
			// relative path only (see resolveAttachedListingMergeTags.ts's
			// resolveByPath - listingurl wins there too when it has a value).
			url: displayListing.listingurl || displayListing.url || 'https://example.com/listing',
		};

		// Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
		// in production the raw tag is returned unchanged for send-time resolution.
		const resolveColor = (key: string, fallback: string) =>
			resolvePreviewColor(data.attributes[key], params.mode, params.dataSource) ||
			fallback;

		const templateContext = {
			// Essential CSS classes for block selection
			cssClasses,
			// Production-only: the real binding spliced onto the root <table> itself,
			// same treatment Image/index.tsx gives data-dynamic-tag on the <img>.
			listingRefAttr:
				params.mode === 'production' ? buildListingDataAttr({ ids: boundListingId }) : '',
			// Block attributes mapped to handlebars context
			backgroundColor: resolveColor('background-color', 'Transparent').toLowerCase(),
			listingNameColor: resolveColor('listing-name-color', '#000000'),
			listingPriceColor: resolveColor('listing-price-color', '#000000'),
			listingAddressColor: resolveColor('listing-address-color', '#000000'),
			listingDetailsColor: resolveColor('listing-details-color', '#666666'),
			titleFontSize: data.attributes['title-font-size'] || '16px',
			priceFontSize: data.attributes['price-font-size'] || '18px',
			addressFontSize: data.attributes['address-font-size'] || '14px',
			buttonColor: resolveColor('button-color', '#007bff'),
			buttonTextColor: resolveColor('button-text-color', '#ffffff'),
			buttonFontSize: data.attributes['button-font-size'] || '14px',
			buttonText: data.attributes['button-text'] || 'View Listing',
			padding: data.attributes['padding'] || '0px',
			lineHeight: data.attributes['line-height'] || 'normal',
			title: data.attributes['title'] || 'Single',
			// Preview links the listing's own URL field to the real listing (monday 13122054004).
			href:
				actualMode === 'preview' && isListingUrlTag((data.attributes['href'] as string) || '{{listing.url}}')
					? previewListingUrl(displayListing.listingurl || displayListing.url, params.dataSource?.user?.officeDomain)
					: resolveListingHref(data.attributes['href'] as string, actualMode, '{{listing.url}}'),
			target: data.attributes['target'] || '_blank',

			// Show/hide flags (explicitly default to true)
			showTitle: data.attributes['show-title'] ?? true,
			showAddress: data.attributes['show-address'] ?? true,
			showPrice: data.attributes['show-price'] ?? true,
			showDetails: data.attributes['show-details'] ?? true,

			// Single listing data - structure matches template {{listing.propertyName}}
			listings: [
				shouldUseMergeTags
					? {
							// SEND-TIME SEAM — see docs/LISTING_BINDING_CONTRACT.md
							//
							// These emit as bare {{address}}/{{listPrice}} tags carrying no
							// listing reference, so the HTML alone cannot say WHICH listing
							// it means. The binding (`selectedListingId`) rides in the block
							// JSON instead, and the send pipeline is expected to read it out
							// of etc.json.content and resolve at dispatch.
							//
							// TODO(vignesh): confirm the pipeline resolves from that id and
							// not from the deprecated `selectedListing` object, which is no
							// longer written. If it reads the object, a re-saved design
							// sends blank listing fields.
							//
							// EDIT/PRODUCTION: Use field names that become merge tags
							imagePath:
								actualMode === 'production'
									? 'imagePath'
									: data.data.value.placeholderImage ||
										getListingPlaceholderImage('placeholder_1'),
							address: 'address',
							city: 'city',
							stateProvince: 'stateProvince',
							postalCode: 'postalCode',
							listPrice: 'listPrice',
							beds: 'beds',
							baths: 'baths',
							sqFoot: 'sqFoot',
							url: 'url',
						}
					: {
							// PREVIEW: Use actual data and include prefixed contexts
							...previewListingContext,
							listing: { ...previewListingContext },
							user: params.dataSource?.user || {},
							contact: params.dataSource?.contact || {},
						},
			],
		};

		try {
			let templateString = layoutConfig.data;

			if (actualMode === 'edit') {
				// Always expose {{imagePath}} for Handlebars evaluation — the URL is always
				// resolved to an actual value in templateContext, so the raw wrapper is not needed.
				templateString = templateString.replace(
					/\{\{\{\{raw\}\}\}\}(\{\{imagePath\}\})\{\{\{\{\/raw\}\}\}\}/g,
					'$1',
				);
			}

			// In PREVIEW mode and CBAY edit mode, strip ALL {{{{raw}}}} blocks so Handlebars
			// can evaluate the remaining merge-tag variables with actual listing data.
			if (actualMode === 'preview' || (actualMode === 'edit' && !isDesignAdmin)) {
				templateString = templateString.replace(/\{\{\{\{raw\}\}\}\}/g, '');
				templateString = templateString.replace(/\{\{\{\{\/raw\}\}\}\}/g, '');
			}

			// Compile and render the handlebars template
			const template = Handlebars.compile(templateString);
			let htmlContent = template(templateContext);

			// In testing mode, process HTML to replace merge tag image URLs with placeholders
			if (params.mode === 'testing') {
				htmlContent = processHtmlMergeTags(htmlContent, params.mode);
			}

			// Canvas/preview cards must not be links (monday 13065458913).
			if (actualMode !== 'production') {
				htmlContent = stripEmptyListingHrefs(htmlContent);
			}

			const parentBlockData =
				params.idx && params.context
					? getParentByIdx({ content: params.context }, params.idx)
					: null;

			if (parentBlockData) {
				// If dropped at PAGE/WRAPPER, auto-wrap into Section -> Column using string MJML
				// Similar to how the Listing block handles this to avoid React component wrapping issues
				if (parentBlockData.type === BasicType.PAGE) {
					// NOTE: Only raw, do not use other tags here
					const mjmlContent = `
						<mj-raw>
							${htmlContent}
						</mj-raw>
					`;

					return (
						<BasicBlock
							params={params}
							tag='mj-section'
						>
							{mjmlContent}
						</BasicBlock>
					);
				}

				if (
					parentBlockData.type === BasicType.WRAPPER ||
					parentBlockData.type === AdvancedType.WRAPPER
				) {
					return (
						<BasicBlock
							params={params}
							tag='mj-raw'
						>
							{htmlContent}
						</BasicBlock>
					);
				}

				// If dropped at SECTION/GROUP, wrap into a single Column using string MJML
				if (
					parentBlockData.type === BasicType.SECTION ||
					parentBlockData.type === AdvancedType.SECTION ||
					parentBlockData.type === BasicType.GROUP ||
					parentBlockData.type === AdvancedType.GROUP
				) {
					const mjmlContent = `
						<mj-raw>
							${htmlContent}
						</mj-raw>
          `;

					return (
						<BasicBlock
							params={params}
							tag='mj-raw'
						>
							{htmlContent}
						</BasicBlock>
					);
				}
			}

			const mjmlContent = `
				<mj-raw>
					${htmlContent}
				</mj-raw>
			`;

			// If already in COLUMN/HERO or parent unknown, render content directly
			return (
				<BasicBlock
					params={params}
					tag='mj-wrapper'
				>
					{mjmlContent}
				</BasicBlock>
			);
		} catch (error) {
			console.error('Error rendering handlebars template:', error);
			console.error('Layout config:', layoutConfig);
			console.error('Template context:', templateContext);
			return (
				<BasicBlock
					params={params}
					tag='mj-raw'
				>
					Error rendering template: {(error as Error).message}
				</BasicBlock>
			);
		}
	},
});
